/**
 * Simple Audio Player Service
 * Handles streaming audio playback for KI and PKI modes
 */
class AudioPlayer {
  constructor() {
    this.audioElement = null;
    this.isPlaying = false;
    this.currentStreamUrl = 'https://v2.schmalgsicht.de/stream/';
  }

  /**
   * Initialize audio element and context
   */
  init() {
    if (this.audioElement) return; // Already initialized

    // Create audio element — no AudioContext/Analyser to keep native
    // hardware-accelerated decoding (critical for CarPlay/mobile)
    this.audioElement = new Audio();
    this.audioElement.preload = 'none';

    // Event listeners
    this.audioElement.addEventListener('play', () => {
      this.isPlaying = true;
    });

    this.audioElement.addEventListener('pause', () => {
      this.isPlaying = false;
    });

    this.audioElement.addEventListener('error', (e) => {
      this.isPlaying = false;
    });

    this.audioElement.addEventListener('waiting', () => {
      // Buffering
    });

    this.audioElement.addEventListener('canplay', () => {
      // Ready to play
    });
  }

  /**
   * Warm up the audio player on user interaction (required for mobile)
   * Call this on first user click/tap to unlock audio playback
   */
  async warmUp() {
    this.init();
  }

  /**
   * Wait for playback to start, with auto-retry on stale connection
   */
  async _startPlayback(canRetry) {
    return new Promise((resolve, reject) => {
      const onPlaying = () => {
        this.audioElement.removeEventListener('playing', onPlaying);
        this.audioElement.removeEventListener('error', onError);
        resolve();
      };
      const onError = async (e) => {
        this.audioElement.removeEventListener('playing', onPlaying);
        this.audioElement.removeEventListener('error', onError);
        if (canRetry) {
          // Connection was stale after pause — reload and retry once
          const currentVolume = this.audioElement.volume;
          this.audioElement.src = this.currentStreamUrl;
          this.audioElement.volume = currentVolume;
          try {
            await this._startPlayback(false);
            resolve();
          } catch (retryError) {
            reject(retryError);
          }
        } else {
          reject(e);
        }
      };
      this.audioElement.addEventListener('playing', onPlaying);
      this.audioElement.addEventListener('error', onError);
      this.audioElement.play().catch(onError);
    });
  }

  /**
   * Start playing the stream
   * Resumes from buffer if possible, reloads on stale connection
   * Returns when audio is actually playing (not just buffering)
   */
  async play() {
    this.init();

    // Only reload stream if no source is set yet
    const needsReload = !this.audioElement.src || this.audioElement.src !== this.currentStreamUrl;
    if (needsReload) {
      const currentVolume = this.audioElement ? this.audioElement.volume : 0.7;
      this.audioElement.src = this.currentStreamUrl;
      this.audioElement.volume = currentVolume;
    }

    await this._startPlayback(!needsReload);
  }

  /**
   * Pause the stream
   */
  pause() {
    if (this.audioElement) {
      this.audioElement.pause();
    }
  }

  /**
   * Stop and reset the stream
   */
  stop() {
    if (this.audioElement) {
      this.audioElement.pause();
      this.audioElement.currentTime = 0;
      this.audioElement.src = '';
      this.isPlaying = false;
    }
  }

  /**
   * Set volume (0-1)
   */
  setVolume(volume) {
    if (this.audioElement) {
      this.audioElement.volume = Math.max(0, Math.min(1, volume));
    }
  }

  /**
   * Switch to a new stream URL and start playing
   * Returns when audio is actually playing (not just buffering)
   * @param {string} newUrl - The new stream URL to play
   */
  async switchStream(newUrl) {
    if (!newUrl) {
      return;
    }

    this.init();
    this.currentStreamUrl = newUrl;

    const currentVolume = this.audioElement ? this.audioElement.volume : 0.7;
    this.audioElement.src = newUrl;
    this.audioElement.volume = currentVolume;

    await this._startPlayback(false);
  }

  /**
   * Get current playing state
   */
  getIsPlaying() {
    return this.isPlaying;
  }

  /**
   * Clean up resources
   */
  dispose() {
    this.stop();
    this.audioElement = null;
  }
}

// Create singleton instance
const audioPlayer = new AudioPlayer();

export default audioPlayer;
